import {
  CONTRAST_THRESHOLDS,
  MARK_SURFACE_VARS,
  parseSourceColorChannels,
  pickOnSurface,
  STATUS_MARK_RAMPS,
  worstContrast,
} from '@mfw/common';

import { common, info } from './palette';

/**
 * `--mfw-palette-<family>-onSurface` — the brand or status colour at the step
 * a reader can actually make out on the CURRENT surface — and the accessor for
 * naming it from application code.
 *
 * This var cannot live in the catalog, and therefore cannot get a `cssVar`
 * accessor in `@mfw/common`:
 *
 *   • The catalog's derivation engine transforms ONE source var. This picks
 *     between up to four steps of a ramp by MEASUREMENT, so there is no single
 *     source to declare.
 *   • `cssVar`/`themeVarName` are pinned 1:1 to the catalog (`theme-vars.spec.ts`,
 *     "has exactly one entry per catalog var"), which is what keeps the typed
 *     API from drifting. A non-catalog entry there would break that.
 *
 * So it is written straight onto `:root` — measured by `applyOnSurfaceRamps`
 * for the six families with catalog ramps, and aliased in
 * `assets/scss/main.scss` both as the pre-JS default and, for `info`, as the
 * only producer. Call sites used to hand-type the var name, 26 times, where a
 * typo is a silently invalid CSS colour: the browser drops the declaration and
 * the text inherits, with nothing failing.
 *
 * `onSurface.primary` reads beside `'text.primary'` and a wrong family is a
 * compile error. `onSurface.spec.ts` binds this list to both producers in both
 * directions.
 */

/**
 * The ramp each family is measured along, best-first. Lives here rather than in
 * `AppThemeProvider` so the table that PRODUCES these vars and the accessor
 * that NAMES them cannot fall out of step.
 *
 * `lighter` is deliberately excluded everywhere: it is a tint for backgrounds,
 * and admitting it would let a step through that no surface can carry.
 *
 * `secondary` stops at `dark` for the reason its mark ramp does: the catalog's
 * `light`/`lighter` are orange and its `darker` is purple, so walking on would
 * turn blue text orange. `main` first (a dark ladder reads it), then `dark`
 * (`#3f51b5`, what a white tile can read); if neither clears 4.5:1 the body ink
 * takes over, as for any family.
 */
export const ON_SURFACE_RAMPS: Record<string, readonly string[]> = {
  accent: ['main', 'light', 'dark'],
  error: ['light', 'main', 'dark', 'darker'],
  primary: ['main', 'light', 'dark', 'darker'],
  secondary: ['main', 'dark'],
  success: ['light', 'main', 'dark', 'darker'],
  warning: ['light', 'main', 'dark', 'darker'],
};

export type ThemeValueReader = (varName: string) => string | undefined;

/**
 * Measures and writes every runtime `onSurface` alias from the theme values
 * that are effective right now. A reader keeps the calculation shared between
 * the resolved-record apply path and the editor's live DOM preview.
 */
export function applyOnSurfaceRamps(
  root: HTMLElement,
  readThemeValue: ThemeValueReader,
) {
  // Independent of the page surface: an island is dark on every palette.
  applyOnIslandRamps(root, readThemeValue);

  const surface = readThemeValue('--mfw-palette-background-default');
  if (!surface) return;

  const fallback = readThemeValue('--mfw-palette-text-primary');
  for (const [family, steps] of Object.entries(ON_SURFACE_RAMPS)) {
    const candidates = steps
      .map((step) => readThemeValue(`--mfw-palette-${family}-${step}`))
      .filter((value): value is string => Boolean(value));
    if (candidates.length === 0) continue;

    root.style.setProperty(
      `--mfw-palette-${family}-onSurface`,
      pickOnSurface(candidates, surface, { fallback }),
    );
  }

  applyOnBrandWash(root, readThemeValue, surface, fallback);
  applyOnSurfaceMarks(root, readThemeValue);
}

/**
 * How strongly a highlighted area is tinted with the brand — the calendar's
 * "today" cell paints `rgba(primary-main / 0.12)` straight over the page. The
 * stylesheet that draws the wash and the measurement below must agree on it,
 * which `onSurface.spec.ts` checks.
 */
export const BRAND_WASH_ALPHA = 0.12;

/**
 * `ink` laid over `surface` at `alpha`, as the browser composites it.
 *
 * Parsed with the engine's own parser, so every format a theme can store —
 * `#RGB`, `#RRGGBB`, `rgb()`, `rgba()` (a tenant's seeded palette is kept
 * verbatim) — composites; `undefined` only for a value no theme layer could
 * paint either. Also how a chart's value-scaled bar is measured
 * (`opacityFloor`): a translucent fill is the same composite.
 */
export const washOver = (ink: string, surface: string, alpha: number) => {
  try {
    const [top, bottom] = [ink, surface].map(parseSourceColorChannels);
    return `#${top
      .map((channel, i) =>
        Math.round(channel * alpha + bottom[i] * (1 - alpha))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')}`;
  } catch {
    return undefined;
  }
};

/**
 * `--mfw-palette-primary-onWash` — the brand as TEXT on its own wash (the
 * "today" cell). `onSurface` is measured against the bare page; the wash
 * shifts the page towards the brand, so a step that just clears 4.5:1 there
 * can fall under it here (`#0e8a7f`: 4.60 on the page, 4.11 on the wash).
 * Measured against the composited wash instead, with the same ramp and the
 * same body-ink fallback.
 *
 * ALWAYS written. Skipping the write when the wash cannot be composited would
 * leave whatever the previous theme measured on the root — a super admin
 * switching tenants kept the last tenant's brand on the today cell (1.54:1).
 * Without a wash, the step is measured against the page, which is what
 * `onSurface` does.
 */
function applyOnBrandWash(
  root: HTMLElement,
  readThemeValue: ThemeValueReader,
  surface: string,
  fallback: string | undefined,
) {
  const main = readThemeValue('--mfw-palette-primary-main');
  const wash = main ? washOver(main, surface, BRAND_WASH_ALPHA) : undefined;

  const candidates = ON_SURFACE_RAMPS.primary
    .map((step) => readThemeValue(`--mfw-palette-primary-${step}`))
    .filter((value): value is string => Boolean(value));
  root.style.setProperty(
    '--mfw-palette-primary-onWash',
    pickOnSurface(candidates, wash ?? surface, { fallback }),
  );
}

/** `onBrandWash.primary` -> `var(--mfw-palette-primary-onWash)`. */
export const onBrandWash = {
  primary: 'var(--mfw-palette-primary-onWash)',
} as const;

/**
 * The families whose colour is drawn as a NON-TEXT mark — a calendar card's
 * edge stripe, a day dot, a ring, an icon — and the ramp each walks, best-first.
 *
 * Separate from `ON_SURFACE_RAMPS` on two counts. WCAG asks 3:1 of a mark, not
 * the 4.5:1 text needs. And the walk starts at `main`: on the default dark
 * ladder every `main` here already clears 3:1, which is the colour the app has
 * always drawn its marks in, so a mark only moves on a surface where `main`
 * cannot be made out. `dark` comes next (the light ladders), then `light` (a
 * mid-tone `main` cannot clear), then `darker`.
 *
 * `secondary` stops at `dark`: the catalog's `light`/`lighter` are orange and
 * its `darker` is purple, so walking on would recolour a blue booking stripe.
 *
 * `primary` and `accent` are the brand as a mark: a Brand-mode calendar draws
 * workouts and bookings in them, and shades nutrition off the primary mark
 * (`theme/scheduleMarks.ts`). Same rules, same `main`-first walk; the accent
 * ramp has no `darker` step to walk on to.
 */
export const ON_SURFACE_MARK_RAMPS = {
  accent: ['main', 'dark', 'light'],
  error: STATUS_MARK_RAMPS.error,
  info: STATUS_MARK_RAMPS.info,
  primary: ['main', 'dark', 'light', 'darker'],
  secondary: STATUS_MARK_RAMPS.secondary,
  success: STATUS_MARK_RAMPS.success,
  warning: STATUS_MARK_RAMPS.warning,
} as const satisfies Record<string, readonly string[]>;

export type OnSurfaceMarkFamily = keyof typeof ON_SURFACE_MARK_RAMPS;

export const ON_SURFACE_MARK_FAMILIES = Object.keys(
  ON_SURFACE_MARK_RAMPS,
) as OnSurfaceMarkFamily[];

/**
 * The surfaces a mark is measured against (`MARK_SURFACE_VARS`: the page AND
 * the cards on it), as the theme holds them right now — the page first. Empty
 * when the theme carries none, which leaves nothing to measure against.
 */
export const readMarkSurfaces = (readThemeValue: ThemeValueReader) =>
  MARK_SURFACE_VARS.map(readThemeValue).filter((value): value is string =>
    Boolean(value),
  );

/**
 * `info` has no catalog vars (see `ON_SURFACE_FAMILIES` below), so the theme
 * record cannot hand its steps over; they are the static `palette.ts` literals.
 */
export const readMarkStep = (
  family: OnSurfaceMarkFamily,
  step: string,
  readThemeValue: ThemeValueReader,
) =>
  family === 'info'
    ? info[step as keyof typeof info]
    : readThemeValue(`--mfw-palette-${family}-${step}`);

/**
 * The candidate's contrast on the surface where it stands out least — shared
 * with the editor's background warnings (`@mfw/common`), re-exported here for
 * the call sites that measure a mark.
 */
export { worstContrast };

/**
 * The mark walk: the first candidate that clears 3:1 on every surface, else
 * the one that stands out best on its worst surface (ties keep the earlier
 * candidate). `undefined` only for no candidates.
 *
 * One rule for every mark measured in JS — `measureOnSurfaceMarks` below and
 * the chart series (`allFeatures/Stats/chartColours.ts`) — so a legend swatch
 * and the series it keys cannot be picked differently.
 */
export const pickMark = (
  candidates: readonly string[],
  surfaces: readonly string[],
) => {
  const scored = candidates.map((color) => ({
    color,
    worst: worstContrast(color, surfaces),
  }));
  const picked =
    scored.find(({ worst }) => worst >= CONTRAST_THRESHOLDS.ui) ??
    scored.reduce<(typeof scored)[number] | undefined>(
      (best, entry) => (!best || entry.worst > best.worst ? entry : best),
      undefined,
    );
  return picked?.color;
};

/**
 * The mark step of every family, measured from the theme values in force —
 * without writing anything. `applyOnSurfaceMarks` writes these as the
 * `onSurfaceMark` aliases; the schedule writer (`theme/scheduleMarks.ts`)
 * builds the calendar's tones from the same picks, so the two cannot measure
 * one theme differently. A family with no readable step, or a theme with no
 * surface to measure against, is absent.
 */
export function measureOnSurfaceMarks(readThemeValue: ThemeValueReader) {
  const marks: Partial<Record<OnSurfaceMarkFamily, string>> = {};
  const surfaces = readMarkSurfaces(readThemeValue);
  if (surfaces.length === 0) return marks;

  const fallback = readThemeValue('--mfw-palette-text-primary');
  for (const family of ON_SURFACE_MARK_FAMILIES) {
    const candidates = ON_SURFACE_MARK_RAMPS[family]
      .map((step) => readMarkStep(family, step, readThemeValue))
      .filter((value): value is string => Boolean(value));
    if (candidates.length === 0) continue;

    // Body ink is the last resort: a mark that loses its hue still marks, where
    // an invisible one does not. But it is derived from the PAGE alone, so it
    // is measured like any step — on a ladder mixing a dark page with a white
    // card it is white on that card. When nothing clears 3:1 everywhere, the
    // candidate that stands out best on its worst surface wins (ties keep the
    // ramp step, which comes first).
    marks[family] = pickMark(
      fallback ? [...candidates, fallback] : candidates,
      surfaces,
    );
  }
  return marks;
}

function applyOnSurfaceMarks(
  root: HTMLElement,
  readThemeValue: ThemeValueReader,
) {
  const marks = measureOnSurfaceMarks(readThemeValue);
  for (const family of ON_SURFACE_MARK_FAMILIES) {
    const mark = marks[family];
    if (mark) {
      root.style.setProperty(`--mfw-palette-${family}-onSurfaceMark`, mark);
    }
  }
}

/**
 * `--mfw-palette-<family>-onIsland` — the brand or status colour as TEXT inside
 * a dark island (the ambient hero, the Fitness Score and stats cards; see
 * `theme/darkIsland.ts`), at the step measured against that island.
 *
 * Not `onSurface`, which is measured against the PAGE: on a light tenant that
 * is a dark step (2.65:1 on the hero), and pinning the island to `light`
 * instead only works for a bright brand — `primary-light` sits 0.07 OKLCH
 * lightness above `main`, so a navy, purple, red or near-black brand stays dark
 * (1.07–3.4:1 on its own ambient wash, where the page-measured step had fallen
 * back to white at 13.9:1).
 *
 * The island's surfaces are the two stops of its wash, `ambient-brand` and
 * `ambient-accent`; a step has to clear 4.5:1 on BOTH. `light` first, as on any
 * dark ladder, then `main`, then the island's own ink (`common.onDark`), which
 * is white on every palette and always clears it.
 *
 * `secondary` is the exception to "`light` first": the catalog's `light` is
 * ORANGE, so the walk is `main` alone (the blue the app draws it in), and the
 * island's own ink where `main` cannot be read on the wash.
 */
export const ON_ISLAND_RAMPS = {
  accent: ['light', 'main'],
  error: ['light', 'main'],
  info: ['light', 'main'],
  primary: ['light', 'main'],
  secondary: ['main'],
  success: ['light', 'main'],
  warning: ['light', 'main'],
} as const satisfies Record<string, readonly string[]>;

export type OnIslandFamily = keyof typeof ON_ISLAND_RAMPS;

export const ON_ISLAND_FAMILIES = Object.keys(
  ON_ISLAND_RAMPS,
) as OnIslandFamily[];

/** The two stops of the ambient wash every island is painted with. */
const ISLAND_SURFACE_VARS = [
  '--mfw-palette-ambient-brand',
  '--mfw-palette-ambient-accent',
];

/**
 * The island's surfaces as the theme holds them right now. Empty when the
 * theme carries none, which leaves nothing to measure against.
 */
export const readIslandSurfaces = (readThemeValue: ThemeValueReader) =>
  ISLAND_SURFACE_VARS.map(readThemeValue).filter((value): value is string =>
    Boolean(value),
  );

/** The island's own ink: white on every palette, so it always clears its wash. */
const readIslandInk = (readThemeValue: ThemeValueReader) =>
  readThemeValue('--mfw-palette-common-onDark') ?? common.white;

/**
 * `--mfw-palette-<family>-onIslandMark` — the brand or status colour as a
 * NON-TEXT mark inside a dark island (a chart series and its legend swatch, a
 * level scale, a sleep bar, a day dot), at the step measured against that
 * island. The island re-points `onSurfaceMark` to it (`theme/darkIsland.ts`).
 *
 * The mark walk (`pickMark`, 3:1) over the island's two wash stops. `main`
 * first, as for any mark — the colour the app has always drawn it in, and what
 * the default brand keeps; then `light`, which is what a dark island reads;
 * `dark`/`darker` never would. `secondary` stops at its blue `main`: its
 * `light` is orange. The island's own ink (`common.onDark`) is the last
 * candidate, so a brand dark at every step (navy is 1.02:1 on its own wash)
 * still marks.
 */
export const ON_ISLAND_MARK_RAMPS = {
  accent: ['main', 'light'],
  error: ['main', 'light'],
  info: ['main', 'light'],
  primary: ['main', 'light'],
  secondary: ['main'],
  success: ['main', 'light'],
  warning: ['main', 'light'],
} as const satisfies Record<OnSurfaceMarkFamily, readonly string[]>;

export type OnIslandMarkFamily = keyof typeof ON_ISLAND_MARK_RAMPS;

export const ON_ISLAND_MARK_FAMILIES = Object.keys(
  ON_ISLAND_MARK_RAMPS,
) as OnIslandMarkFamily[];

/**
 * The island mark of every family, measured from the theme values in force —
 * without writing anything. `applyOnIslandRamps` writes these as the
 * `onIslandMark` aliases; a chart inside an island draws its series in the
 * same picks (`allFeatures/Stats/chartColours.ts`), so a series and the legend
 * swatch that keys it cannot be measured differently. Empty for a theme with no
 * wash to measure against.
 */
export function measureOnIslandMarks(readThemeValue: ThemeValueReader) {
  const marks: Partial<Record<OnIslandMarkFamily, string>> = {};
  const surfaces = readIslandSurfaces(readThemeValue);
  if (surfaces.length === 0) return marks;

  const ink = readIslandInk(readThemeValue);
  for (const family of ON_ISLAND_MARK_FAMILIES) {
    const candidates = ON_ISLAND_MARK_RAMPS[family]
      .map((step) => readMarkStep(family, step, readThemeValue))
      .filter((value): value is string => Boolean(value));
    marks[family] = pickMark([...candidates, ink], surfaces);
  }
  return marks;
}

function applyOnIslandRamps(
  root: HTMLElement,
  readThemeValue: ThemeValueReader,
) {
  const surfaces = readIslandSurfaces(readThemeValue);
  if (surfaces.length === 0) return;

  const ink = readIslandInk(readThemeValue);
  for (const family of ON_ISLAND_FAMILIES) {
    const candidates = ON_ISLAND_RAMPS[family]
      .map((step) =>
        family === 'info'
          ? info[step]
          : readThemeValue(`--mfw-palette-${family}-${step}`),
      )
      .filter((value): value is string => Boolean(value));
    const picked =
      candidates.find(
        (color) => worstContrast(color, surfaces) >= CONTRAST_THRESHOLDS.text,
      ) ?? ink;
    root.style.setProperty(`--mfw-palette-${family}-onIsland`, picked);
  }

  const marks = measureOnIslandMarks(readThemeValue);
  for (const family of ON_ISLAND_MARK_FAMILIES) {
    const mark = marks[family];
    if (mark) {
      root.style.setProperty(`--mfw-palette-${family}-onIslandMark`, mark);
    }
  }
}

/** `onIsland.primary` -> `var(--mfw-palette-primary-onIsland)`. */
export const onIsland = Object.fromEntries(
  ON_ISLAND_FAMILIES.map((family) => [
    family,
    `var(--mfw-palette-${family}-onIsland)`,
  ]),
) as Record<OnIslandFamily, string>;

/** `onIslandMark.primary` -> `var(--mfw-palette-primary-onIslandMark)`. */
export const onIslandMark = Object.fromEntries(
  ON_ISLAND_MARK_FAMILIES.map((family) => [
    family,
    `var(--mfw-palette-${family}-onIslandMark)`,
  ]),
) as Record<OnIslandMarkFamily, string>;

/** `onSurfaceMark.info` -> `var(--mfw-palette-info-onSurfaceMark)`. */
export const onSurfaceMark = Object.fromEntries(
  ON_SURFACE_MARK_FAMILIES.map((family) => [
    family,
    `var(--mfw-palette-${family}-onSurfaceMark)`,
  ]),
) as Record<OnSurfaceMarkFamily, string>;

/**
 * Every family with an `onSurface` var at runtime.
 *
 * `info` is here but NOT in the ramps above: it has no catalog vars at all — it
 * is a static family in `palette.ts` — so JS has nothing to measure for it and
 * `main.scss` is its only producer, flipping on the surface flag.
 */
export const ON_SURFACE_FAMILIES = [
  'accent',
  'error',
  'info',
  'primary',
  'secondary',
  'success',
  'warning',
] as const;

export type OnSurfaceFamily = (typeof ON_SURFACE_FAMILIES)[number];

/** `onSurface.primary` -> `var(--mfw-palette-primary-onSurface)`. */
export const onSurface = Object.fromEntries(
  ON_SURFACE_FAMILIES.map((family) => [
    family,
    `var(--mfw-palette-${family}-onSurface)`,
  ]),
) as Record<OnSurfaceFamily, string>;
